import { useState, useMemo, useRef, useEffect } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  TooltipProps,
} from 'recharts';
import { useThemeColors } from '../hooks/useThemeColors';
import {
  getOverviewStats,
  getPerformanceData,
  getTopCampaigns,
  getTopCountries,
  getAdTypeStats,
  getPlatformStats,
  getFlowCompare,
  formatNumber,
  type OverviewStats,
  type PerformanceDataPoint,
  type AdTypeStats,
  type PlatformStats,
  type StatsFilters,
  type FlowCompareData,
} from '@/api/stats';
import {
  getReferralOverview,
  getReferralDaily,
  getReferralByCountry,
  type ReferralOverview,
  type ReferralDailyStats,
  type ReferralByCountry,
} from '@/api/referrals';
import { getApps } from '@/api/apps';
import type { App } from '@/api/types';
import { formatChartDay } from '@/lib/datetime';
import { useQuery } from '@tanstack/react-query';

// --- Date range ---
type DateRange = '7d' | '30d' | '90d' | 'custom';

const dateRangeOptions: { value: DateRange; label: string; days: number }[] = [
  { value: '7d', label: '7 Days', days: 7 },
  { value: '30d', label: '30 Days', days: 30 },
  { value: '90d', label: '90 Days', days: 90 },
  { value: 'custom', label: 'Custom', days: 0 },
];

interface ExtendedPerformanceData extends PerformanceDataPoint {
  ctr: number;
  ref_clicks: number;
  ref_delivered: number;
}

// --- Custom dropdown component ---
interface DropdownOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

function Dropdown({
  value,
  options,
  onChange,
  placeholder,
  icon,
  searchable = false,
}: {
  value: string;
  options: DropdownOption[];
  onChange: (v: string) => void;
  placeholder: string;
  icon?: React.ReactNode;
  searchable?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (open && searchable) {
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [open, searchable]);

  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.value === 'all' || o.label.toLowerCase().includes(q));
  }, [options, search]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => { setOpen(!open); if (open) setSearch(''); }}
        className={`flex items-center gap-2 pl-3 pr-2.5 py-2 rounded-lg text-sm font-medium border transition-all duration-150 min-w-[140px] ${
          open
            ? 'border-primary ring-2 ring-primary/40 bg-surface shadow-sm'
            : value !== 'all'
              ? 'border-primary/30 bg-primary/10 text-primary'
              : 'border-border bg-surface text-content-muted hover:border-border-strong hover:shadow-sm'
        }`}
      >
        {icon && <span className="text-content-subtle flex-shrink-0">{icon}</span>}
        <span className="truncate flex-1 text-left">
          {selected?.label || placeholder}
        </span>
        <svg
          className={`w-4 h-4 text-content-subtle flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-50 mt-1.5 w-full min-w-[220px] bg-surface border border-border rounded-xl shadow-lg overflow-hidden">
          {searchable && (
            <div className="p-2 border-b border-border">
              <div className="relative">
                <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-content-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search..."
                  className="w-full pl-8 pr-3 py-1.5 text-sm bg-surface-muted border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary/40 placeholder:text-content-subtle"
                />
              </div>
            </div>
          )}
          <div className="max-h-60 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-content-subtle">No results</div>
            ) : (
              filtered.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                    setSearch('');
                  }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors ${
                    opt.value === value
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-content-muted hover:bg-surface-muted'
                  }`}
                >
                  {opt.icon && <span className="flex-shrink-0">{opt.icon}</span>}
                  <span className="truncate">{opt.label}</span>
                  {opt.value === value && (
                    <svg className="w-4 h-4 ml-auto text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Tooltip and ChartCard moved into CombinedChart ---

function formatPercentage(value: number): string {
  return `${value.toFixed(2)}%`;
}

// --- Combined Chart with toggles ---
const chartSeries = [
  { key: 'impressions', label: 'Impressions', color: '#635BFF', type: 'area' as const },
  { key: 'clicks', label: 'Clicks', color: '#06B6D4', type: 'area' as const },
  { key: 'completes', label: 'Completes', color: '#10B981', type: 'area' as const },
  { key: 'ctr', label: 'CTR', color: '#F59E0B', type: 'line' as const },
  { key: 'ref_clicks', label: 'Ref Clicks', color: '#EC4899', type: 'area' as const },
  { key: 'ref_delivered', label: 'Ref Delivered', color: '#8B5CF6', type: 'area' as const },
];

function CombinedChart({ data }: { data: ExtendedPerformanceData[] }) {
  const themeColors = useThemeColors();
  const [visible, setVisible] = useState<Record<string, boolean>>({
    impressions: true,
    clicks: true,
    completes: true,
    ctr: true,
    ref_clicks: false,
    ref_delivered: false,
  });

  const toggle = (key: string) => setVisible((prev) => ({ ...prev, [key]: !prev[key] }));

  const showRightAxis = visible.ctr;
  const showLeftAxis = visible.impressions || visible.clicks || visible.completes || visible.ref_clicks || visible.ref_delivered;

  const CombinedTooltip = ({
    active,
    payload,
    label,
  }: TooltipProps<number, string>) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-surface/95 backdrop-blur border border-border rounded-xl shadow-lg p-3 min-w-[160px]">
          <p className="text-xs font-medium text-content-subtle mb-1.5">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm flex items-center gap-2" style={{ color: entry.color }}>
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
              {entry.name}:{' '}
              <span className="font-semibold ml-auto">
                {entry.dataKey === 'ctr' ? formatPercentage(entry.value as number) : formatNumber(entry.value as number)}
              </span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-surface rounded-xl border border-border shadow-sm p-5 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-content">Performance Overview</h3>
        <div className="flex items-center gap-2">
          {chartSeries.map((s) => (
            <button
              key={s.key}
              onClick={() => toggle(s.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                visible[s.key]
                  ? 'bg-surface-muted text-content shadow-sm'
                  : 'bg-transparent text-content-subtle hover:text-content-subtle'
              }`}
            >
              <span
                className="w-2.5 h-2.5 rounded-full transition-opacity"
                style={{
                  backgroundColor: s.color,
                  opacity: visible[s.key] ? 1 : 0.3,
                }}
              />
              {s.label}
            </button>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={360}>
        <ComposedChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="gImp" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#635BFF" stopOpacity={0.15} />
              <stop offset="95%" stopColor="#635BFF" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gClk" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#06B6D4" stopOpacity={0.15} />
              <stop offset="95%" stopColor="#06B6D4" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gCmp" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#10B981" stopOpacity={0.15} />
              <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gRefClk" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#EC4899" stopOpacity={0.15} />
              <stop offset="95%" stopColor="#EC4899" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gRefDlv" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.15} />
              <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={themeColors.grid} />
          <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: themeColors.axis }} />
          {showLeftAxis && (
            <YAxis
              yAxisId="left"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: themeColors.axis }}
              tickFormatter={(v: number) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v))}
            />
          )}
          {showRightAxis && (
            <YAxis
              yAxisId="right"
              orientation="right"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: '#F59E0B' }}
              tickFormatter={(v: number) => `${v.toFixed(1)}%`}
              domain={[0, 'auto']}
            />
          )}
          <Tooltip content={<CombinedTooltip />} />
          {visible.impressions && (
            <Area yAxisId="left" type="monotone" dataKey="impressions" name="Impressions" stroke="#635BFF" strokeWidth={2} fill="url(#gImp)" />
          )}
          {visible.clicks && (
            <Area yAxisId="left" type="monotone" dataKey="clicks" name="Clicks" stroke="#06B6D4" strokeWidth={2} fill="url(#gClk)" />
          )}
          {visible.completes && (
            <Area yAxisId="left" type="monotone" dataKey="completes" name="Completes" stroke="#10B981" strokeWidth={2} fill="url(#gCmp)" />
          )}
          {visible.ref_clicks && (
            <Area yAxisId="left" type="monotone" dataKey="ref_clicks" name="Ref Clicks" stroke="#EC4899" strokeWidth={2} fill="url(#gRefClk)" />
          )}
          {visible.ref_delivered && (
            <Area yAxisId="left" type="monotone" dataKey="ref_delivered" name="Ref Delivered" stroke="#8B5CF6" strokeWidth={2} fill="url(#gRefDlv)" />
          )}
          {visible.ctr && (
            <Line yAxisId="right" type="monotone" dataKey="ctr" name="CTR" stroke="#F59E0B" strokeWidth={2} dot={{ fill: '#F59E0B', strokeWidth: 0, r: 3 }} activeDot={{ r: 5 }} />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// --- Platform constants ---
const platformLabels: Record<string, string> = { ios: 'iOS', android: 'Android', unknown: 'Unknown' };
const platformColors: Record<string, string> = { ios: 'bg-surface-muted', android: 'bg-success-soft', unknown: 'bg-surface-muted' };
const platformBarColors: Record<string, string> = { ios: 'bg-content-subtle', android: 'bg-success', unknown: 'bg-content-subtle' };

// --- Icons ---
const AppIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
  </svg>
);
const GlobeIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);
const PlatformIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);
const CampaignIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2z" />
  </svg>
);

// --- Stats Card ---
function StatCard({
  label,
  value,
  change,
  trend,
  color,
}: {
  label: string;
  value: string;
  change?: string;
  trend?: 'up' | 'down' | 'neutral';
  color: string;
}) {
  return (
    <div className="bg-surface rounded-xl border border-border shadow-sm p-5 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium text-content-subtle">{label}</p>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
          {label === 'Impressions' && (
            <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          )}
          {label === 'Clicks' && (
            <svg className="w-4 h-4 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
            </svg>
          )}
          {label === 'CTR' && (
            <svg className="w-4 h-4 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          )}
          {label === 'Completes' && (
            <svg className="w-4 h-4 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          {label === 'Ref Clicks' && (
            <svg className="w-4 h-4 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
          )}
          {label === 'Delivered' && (
            <svg className="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
          {label === 'Expired' && (
            <svg className="w-4 h-4 text-danger" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          {label === 'Delivery Rate' && (
            <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          )}
        </div>
      </div>
      <p className="text-2xl font-bold text-content font-mono tracking-tight">{value}</p>
      {change && trend && (
        <div className="flex items-center mt-2 gap-1">
          <span
            className={`inline-flex items-center gap-0.5 text-xs font-semibold px-1.5 py-0.5 rounded-md ${
              trend === 'up'
                ? 'bg-success-soft text-success'
                : trend === 'down'
                  ? 'bg-danger-soft text-danger'
                  : 'bg-surface-muted text-content-subtle'
            }`}
          >
            {trend === 'up' && (
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
            )}
            {trend === 'down' && (
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            )}
            {change}
          </span>
          <span className="text-xs text-content-subtle">vs prev period</span>
        </div>
      )}
    </div>
  );
}

// --- Full country list for dropdown ---
const allCountries: { code: string; name: string }[] = [
  { code: 'AF', name: 'Afghanistan' }, { code: 'AL', name: 'Albania' }, { code: 'DZ', name: 'Algeria' },
  { code: 'AR', name: 'Argentina' }, { code: 'AM', name: 'Armenia' }, { code: 'AU', name: 'Australia' },
  { code: 'AT', name: 'Austria' }, { code: 'AZ', name: 'Azerbaijan' }, { code: 'BH', name: 'Bahrain' },
  { code: 'BD', name: 'Bangladesh' }, { code: 'BY', name: 'Belarus' }, { code: 'BE', name: 'Belgium' },
  { code: 'BO', name: 'Bolivia' }, { code: 'BA', name: 'Bosnia' }, { code: 'BR', name: 'Brazil' },
  { code: 'BG', name: 'Bulgaria' }, { code: 'KH', name: 'Cambodia' }, { code: 'CA', name: 'Canada' },
  { code: 'CL', name: 'Chile' }, { code: 'CN', name: 'China' }, { code: 'CO', name: 'Colombia' },
  { code: 'CR', name: 'Costa Rica' }, { code: 'HR', name: 'Croatia' }, { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czech Republic' }, { code: 'DK', name: 'Denmark' }, { code: 'DO', name: 'Dominican Republic' },
  { code: 'EC', name: 'Ecuador' }, { code: 'EG', name: 'Egypt' }, { code: 'SV', name: 'El Salvador' },
  { code: 'EE', name: 'Estonia' }, { code: 'ET', name: 'Ethiopia' }, { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' }, { code: 'GE', name: 'Georgia' }, { code: 'DE', name: 'Germany' },
  { code: 'GH', name: 'Ghana' }, { code: 'GR', name: 'Greece' }, { code: 'GT', name: 'Guatemala' },
  { code: 'HN', name: 'Honduras' }, { code: 'HK', name: 'Hong Kong' }, { code: 'HU', name: 'Hungary' },
  { code: 'IS', name: 'Iceland' }, { code: 'IN', name: 'India' }, { code: 'ID', name: 'Indonesia' },
  { code: 'IR', name: 'Iran' }, { code: 'IQ', name: 'Iraq' }, { code: 'IE', name: 'Ireland' },
  { code: 'IL', name: 'Israel' }, { code: 'IT', name: 'Italy' }, { code: 'JM', name: 'Jamaica' },
  { code: 'JP', name: 'Japan' }, { code: 'JO', name: 'Jordan' }, { code: 'KZ', name: 'Kazakhstan' },
  { code: 'KE', name: 'Kenya' }, { code: 'KW', name: 'Kuwait' }, { code: 'KG', name: 'Kyrgyzstan' },
  { code: 'LA', name: 'Laos' }, { code: 'LV', name: 'Latvia' }, { code: 'LB', name: 'Lebanon' },
  { code: 'LT', name: 'Lithuania' }, { code: 'LU', name: 'Luxembourg' }, { code: 'MY', name: 'Malaysia' },
  { code: 'MX', name: 'Mexico' }, { code: 'MD', name: 'Moldova' }, { code: 'MN', name: 'Mongolia' },
  { code: 'MA', name: 'Morocco' }, { code: 'MM', name: 'Myanmar' }, { code: 'NP', name: 'Nepal' },
  { code: 'NL', name: 'Netherlands' }, { code: 'NZ', name: 'New Zealand' }, { code: 'NI', name: 'Nicaragua' },
  { code: 'NG', name: 'Nigeria' }, { code: 'NO', name: 'Norway' }, { code: 'OM', name: 'Oman' },
  { code: 'PK', name: 'Pakistan' }, { code: 'PA', name: 'Panama' }, { code: 'PY', name: 'Paraguay' },
  { code: 'PE', name: 'Peru' }, { code: 'PH', name: 'Philippines' }, { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' }, { code: 'QA', name: 'Qatar' }, { code: 'RO', name: 'Romania' },
  { code: 'RU', name: 'Russia' }, { code: 'SA', name: 'Saudi Arabia' }, { code: 'RS', name: 'Serbia' },
  { code: 'SG', name: 'Singapore' }, { code: 'SK', name: 'Slovakia' }, { code: 'SI', name: 'Slovenia' },
  { code: 'ZA', name: 'South Africa' }, { code: 'KR', name: 'South Korea' }, { code: 'ES', name: 'Spain' },
  { code: 'LK', name: 'Sri Lanka' }, { code: 'SE', name: 'Sweden' }, { code: 'CH', name: 'Switzerland' },
  { code: 'TW', name: 'Taiwan' }, { code: 'TZ', name: 'Tanzania' }, { code: 'TH', name: 'Thailand' },
  { code: 'TN', name: 'Tunisia' }, { code: 'TR', name: 'Turkey' }, { code: 'UA', name: 'Ukraine' },
  { code: 'AE', name: 'UAE' }, { code: 'GB', name: 'United Kingdom' }, { code: 'US', name: 'United States' },
  { code: 'UY', name: 'Uruguay' }, { code: 'UZ', name: 'Uzbekistan' }, { code: 'VE', name: 'Venezuela' },
  { code: 'VN', name: 'Vietnam' },
];

// === Main component ===
export function Analytics() {
  const [dateRange, setDateRange] = useState<DateRange>('7d');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedApp, setSelectedApp] = useState('all');
  const [selectedPlatform, setSelectedPlatform] = useState('all');

  const days = useMemo(() => {
    if (dateRange === 'custom' && customStartDate && customEndDate) {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      return Math.ceil(Math.abs(end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) || 7;
    }
    return dateRangeOptions.find((o) => o.value === dateRange)?.days || 7;
  }, [dateRange, customStartDate, customEndDate]);

  // Filters
  const filters: StatsFilters = useMemo(() => {
    const f: StatsFilters = {};
    if (selectedApp !== 'all') f.app_id = selectedApp;
    if (selectedCampaign !== 'all') f.campaign_id = selectedCampaign;
    if (selectedCountry !== 'all') f.country = selectedCountry;
    if (selectedPlatform !== 'all') f.platform = selectedPlatform;
    return f;
  }, [selectedApp, selectedCampaign, selectedCountry, selectedPlatform]);

  const filtersForCampaigns: StatsFilters = useMemo(() => {
    const { campaign_id: _, ...rest } = filters;
    return rest;
  }, [filters]);

  const filtersForCountries: StatsFilters = useMemo(() => {
    const { country: _, ...rest } = filters;
    return rest;
  }, [filters]);

  const hasActiveFilters = Object.keys(filters).length > 0;

  // Data queries
  const { data: apps = [] } = useQuery<App[]>({
    queryKey: ['analytics-apps'],
    queryFn: getApps,
  });

  const { data: overviewStats } = useQuery<OverviewStats>({
    queryKey: ['analytics-overview', days, filters],
    queryFn: () => getOverviewStats(days, filters),
  });

  const { data: performanceData = [], isLoading: loadingPerformance } = useQuery({
    queryKey: ['analytics-performance', days, filters],
    queryFn: () => getPerformanceData(days, filters),
  });

  const { data: campaigns = [] } = useQuery({
    queryKey: ['analytics-campaigns', days, filtersForCampaigns],
    queryFn: () => getTopCampaigns(days, filtersForCampaigns),
  });

  const { data: countries = [] } = useQuery({
    queryKey: ['analytics-countries', days, filtersForCountries],
    queryFn: () => getTopCountries(days, filtersForCountries),
  });

  const { data: adTypeData = [] } = useQuery<AdTypeStats[]>({
    queryKey: ['analytics-ad-types', days, filters],
    queryFn: () => getAdTypeStats(days, filters),
  });

  const { data: platformData = [] } = useQuery<PlatformStats[]>({
    queryKey: ['analytics-platforms', days, filters],
    queryFn: () => getPlatformStats(days, filters),
  });

  const { data: flowCompare } = useQuery<FlowCompareData | null>({
    queryKey: ['analytics-flow-compare', days],
    queryFn: () => getFlowCompare(days),
  });

  // Referral queries
  const { data: referralOverview } = useQuery<ReferralOverview>({
    queryKey: ['analytics-referral-overview', days],
    queryFn: () => getReferralOverview(days),
  });

  const { data: referralDaily = [] } = useQuery<ReferralDailyStats[]>({
    queryKey: ['analytics-referral-daily', days],
    queryFn: () => getReferralDaily(days),
  });

  const { data: referralCountries = [] } = useQuery<ReferralByCountry[]>({
    queryKey: ['analytics-referral-countries', days],
    queryFn: () => getReferralByCountry(days),
  });

  // Build referral daily lookup by date
  const referralDailyMap = useMemo(() => {
    const map: Record<string, ReferralDailyStats> = {};
    for (const r of referralDaily) {
      // Format date to match performance data format
      const formatted = formatChartDay(r.date);
      map[formatted] = r;
    }
    return map;
  }, [referralDaily]);

  // Computed — merge performance + referral data
  const extendedData: ExtendedPerformanceData[] = useMemo(
    () => performanceData.map((p) => {
      const ref = referralDailyMap[p.date];
      return {
        ...p,
        ctr: p.impressions > 0 ? (p.clicks / p.impressions) * 100 : 0,
        ref_clicks: ref?.clicks || 0,
        ref_delivered: ref?.delivered || 0,
      };
    }),
    [performanceData, referralDailyMap],
  );

  // Dropdown options
  const appOptions: DropdownOption[] = useMemo(
    () => [
      { value: 'all', label: 'All Apps' },
      ...apps.map((a) => ({ value: a.id, label: `${a.name} (${a.platform})` })),
    ],
    [apps],
  );

  const countryOptions: DropdownOption[] = useMemo(
    () => [
      { value: 'all', label: 'All Countries' },
      ...allCountries.map((c) => ({
        value: c.code,
        label: c.name,
        icon: <span className="text-base leading-none">{getFlagEmoji(c.code)}</span>,
      })),
    ],
    [],
  );

  const platformOptions: DropdownOption[] = [
    { value: 'all', label: 'All Platforms' },
    { value: 'android', label: 'Android', icon: <span className="text-success text-xs">&#9679;</span> },
    { value: 'ios', label: 'iOS', icon: <span className="text-content text-xs">&#9679;</span> },
  ];

  const campaignOptions: DropdownOption[] = useMemo(
    () => [
      { value: 'all', label: 'All Campaigns' },
      ...campaigns.map((c) => ({ value: c.id, label: c.name })),
    ],
    [campaigns],
  );

  function clearFilters() {
    setSelectedApp('all');
    setSelectedCampaign('all');
    setSelectedCountry('all');
    setSelectedPlatform('all');
  }

  // Loading
  if (loadingPerformance && !overviewStats) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-surface-muted rounded-lg w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-surface rounded-xl border border-border p-5 h-28">
              <div className="h-3 bg-surface-muted rounded w-20 mb-3" />
              <div className="h-7 bg-surface-muted rounded w-28" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {[1, 2].map((i) => (
            <div key={i} className="bg-surface rounded-xl border border-border p-5 h-80">
              <div className="h-3 bg-surface-muted rounded w-32 mb-4" />
              <div className="h-60 bg-surface-muted rounded-lg" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const hasData = performanceData.some((p) => p.impressions > 0 || p.clicks > 0);

  // No page title here: Home.tsx owns it, along with the Tracker/Ads switch this
  // view sits under, and a second <h1> would just repeat it.
  return (
    <div className="space-y-6">
      {/* Filters bar */}
      <div className="bg-surface rounded-xl border border-border shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Date range pills */}
          <div className="flex bg-surface-muted rounded-lg p-0.5 gap-0.5">
            {dateRangeOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setDateRange(opt.value)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                  dateRange === opt.value
                    ? 'bg-surface text-content shadow-sm'
                    : 'text-content-subtle hover:text-content-muted'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {dateRange === 'custom' && (
            <div className="flex gap-2 items-center">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="px-2.5 py-1.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              />
              <span className="text-content-subtle">&mdash;</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="px-2.5 py-1.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              />
            </div>
          )}

          <div className="w-px h-7 bg-surface-muted" />

          <Dropdown value={selectedApp} options={appOptions} onChange={setSelectedApp} placeholder="All Apps" icon={<AppIcon />} searchable />
          <Dropdown value={selectedCountry} options={countryOptions} onChange={setSelectedCountry} placeholder="All Countries" icon={<GlobeIcon />} searchable />
          <Dropdown value={selectedPlatform} options={platformOptions} onChange={setSelectedPlatform} placeholder="All Platforms" icon={<PlatformIcon />} />
          <Dropdown value={selectedCampaign} options={campaignOptions} onChange={setSelectedCampaign} placeholder="All Campaigns" icon={<CampaignIcon />} searchable />

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-danger hover:bg-danger-soft rounded-lg transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Ad Stats Cards */}
      {overviewStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <StatCard
            label="Impressions"
            value={formatNumber(overviewStats.impressions.value)}
            change={overviewStats.impressions.change}
            trend={overviewStats.impressions.trend}
            color="bg-primary/10"
          />
          <StatCard
            label="Clicks"
            value={formatNumber(overviewStats.clicks.value)}
            change={overviewStats.clicks.change}
            trend={overviewStats.clicks.trend}
            color="bg-cyan-100"
          />
          <StatCard
            label="CTR"
            value={overviewStats.ctr.value}
            change={overviewStats.ctr.change}
            trend={overviewStats.ctr.trend}
            color="bg-warning-soft"
          />
          <StatCard
            label="Completes"
            value={formatNumber(overviewStats.completes.value)}
            change={overviewStats.completes.change}
            trend={overviewStats.completes.trend}
            color="bg-success-soft"
          />
        </div>
      )}

      {/* Referral Stats Cards */}
      {referralOverview && (referralOverview.total_clicks > 0 || referralOverview.delivered > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <StatCard
            label="Ref Clicks"
            value={formatNumber(referralOverview.total_clicks)}
            color="bg-pink-100"
          />
          <StatCard
            label="Delivered"
            value={formatNumber(referralOverview.delivered)}
            color="bg-violet-100"
          />
          <StatCard
            label="Expired"
            value={formatNumber(referralOverview.expired)}
            color="bg-danger-soft"
          />
          <StatCard
            label="Delivery Rate"
            value={`${referralOverview.delivery_rate.toFixed(1)}%`}
            color="bg-teal-100"
          />
        </div>
      )}

      {!hasData ? (
        <div className="bg-surface rounded-xl border border-border shadow-sm p-16 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-surface-muted mb-4">
            <svg className="w-8 h-8 text-content-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <p className="text-content-muted font-medium">No analytics data yet</p>
          <p className="text-sm text-content-subtle mt-1">Data will appear here once your ads start getting impressions</p>
        </div>
      ) : (
        <>
          {/* Combined Chart */}
          <CombinedChart data={extendedData} />

          {/* Platform Breakdown */}
          {platformData.length > 0 && (
            <div className="bg-surface rounded-xl border border-border shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-border">
                <h3 className="text-sm font-semibold text-content">Platform Breakdown</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-surface-muted/50">
                    <tr>
                      {['Platform', 'Impressions', 'Clicks', 'Completes', 'CTR', 'Share'].map((h, i) => (
                        <th key={h} className={`px-6 py-3 text-xs font-semibold text-content-subtle uppercase tracking-wider ${i === 0 ? 'text-left' : 'text-right'}`}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {platformData.map((p) => {
                      const total = platformData.reduce((s, i) => s + i.impressions, 0);
                      const share = total > 0 ? ((p.impressions / total) * 100).toFixed(1) : '0.0';
                      return (
                        <tr key={p.platform} className="hover:bg-surface-muted/50 transition-colors">
                          <td className="px-6 py-3.5">
                            <div className="flex items-center gap-2.5">
                              <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${platformColors[p.platform] || 'bg-surface-muted'}`}>
                                {p.platform === 'ios' && <svg className="w-3.5 h-3.5 text-content" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" /></svg>}
                                {p.platform === 'android' && <svg className="w-3.5 h-3.5 text-success" viewBox="0 0 24 24" fill="currentColor"><path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.27-.86-.31-.16-.69-.04-.86.27l-1.86 3.22c-1.44-.65-3.05-1.01-4.76-1.01-1.71 0-3.32.36-4.76 1.01L5.13 5.71c-.17-.31-.55-.43-.86-.27-.31.17-.43.55-.27.86L5.84 9.48C2.84 11.12.96 13.95.96 17.2h22.08c0-3.25-1.88-6.08-4.88-7.72z" /></svg>}
                              </div>
                              <span className="text-sm font-medium text-content">{platformLabels[p.platform] || p.platform}</span>
                            </div>
                          </td>
                          <td className="px-6 py-3.5 text-right text-sm text-content-muted font-mono">{formatNumber(p.impressions)}</td>
                          <td className="px-6 py-3.5 text-right text-sm text-content-muted font-mono">{formatNumber(p.clicks)}</td>
                          <td className="px-6 py-3.5 text-right text-sm text-content-muted font-mono">{formatNumber(p.completes)}</td>
                          <td className="px-6 py-3.5 text-right text-sm text-content-muted font-mono">{p.ctr.toFixed(2)}%</td>
                          <td className="px-6 py-3.5 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-14 bg-surface-muted rounded-full h-1.5">
                                <div className={`h-1.5 rounded-full ${platformBarColors[p.platform] || 'bg-content-subtle'}`} style={{ width: `${share}%` }} />
                              </div>
                              <span className="text-xs text-content-subtle font-mono w-10">{share}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Campaign, Country & Referral Country tables */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
            {/* Campaigns */}
            <div className="bg-surface rounded-xl border border-border shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-border">
                <h3 className="text-sm font-semibold text-content">Top Campaigns</h3>
              </div>
              {campaigns.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-surface-muted/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-content-subtle uppercase tracking-wider">Campaign</th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-content-subtle uppercase tracking-wider">Impr.</th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-content-subtle uppercase tracking-wider">Clicks</th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-content-subtle uppercase tracking-wider">CTR</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {campaigns.map((c) => (
                        <tr key={c.id} className="hover:bg-surface-muted/50 transition-colors">
                          <td className="px-6 py-3.5 text-sm font-medium text-content">{c.name}</td>
                          <td className="px-6 py-3.5 text-right text-sm text-content-muted font-mono">{formatNumber(c.impressions)}</td>
                          <td className="px-6 py-3.5 text-right text-sm text-content-muted font-mono">{formatNumber(c.clicks)}</td>
                          <td className="px-6 py-3.5 text-right text-sm text-content-muted font-mono">{c.ctr}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="px-6 py-10 text-center text-sm text-content-subtle">No campaign data</div>
              )}
            </div>

            {/* Countries */}
            <div className="bg-surface rounded-xl border border-border shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-border">
                <h3 className="text-sm font-semibold text-content">Top Countries</h3>
              </div>
              {countries.length > 0 ? (
                <div className="p-5 space-y-3">
                  {countries.map((country) => (
                    <div key={country.code} className="flex items-center gap-3">
                      <span className="text-lg leading-none">{getFlagEmoji(country.code)}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-content truncate">{country.name}</span>
                          <div className="flex items-center gap-3 ml-2">
                            <span className="text-xs text-content-subtle font-mono">{formatNumber(country.impressions)}</span>
                            <span className="text-xs font-semibold text-content-muted font-mono w-12 text-right">{country.percentage}%</span>
                          </div>
                        </div>
                        <div className="w-full bg-surface-muted rounded-full h-1.5">
                          <div className="bg-purple-500 h-1.5 rounded-full transition-all" style={{ width: `${country.percentage}%` }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="px-6 py-10 text-center text-sm text-content-subtle">No country data</div>
              )}
            </div>

            {/* Referral Countries */}
            <div className="bg-surface rounded-xl border border-border shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-border">
                <h3 className="text-sm font-semibold text-content">Referral Countries</h3>
              </div>
              {referralCountries.length > 0 ? (
                <div className="p-5 space-y-3">
                  {referralCountries.slice(0, 8).map((rc) => {
                    const maxClicks = referralCountries[0]?.clicks || 1;
                    const pct = ((rc.clicks / maxClicks) * 100).toFixed(1);
                    return (
                      <div key={rc.country} className="flex items-center gap-3">
                        <span className="text-lg leading-none">{getFlagEmoji(rc.country || 'XX')}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-content truncate">{rc.country || 'Unknown'}</span>
                            <div className="flex items-center gap-3 ml-2">
                              <span className="text-xs text-content-subtle font-mono">{formatNumber(rc.clicks)} clicks</span>
                              <span className="text-xs font-semibold text-violet-600 font-mono w-14 text-right">{formatNumber(rc.delivered)} dlv</span>
                            </div>
                          </div>
                          <div className="w-full bg-surface-muted rounded-full h-1.5">
                            <div className="bg-pink-500 h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="px-6 py-10 text-center text-sm text-content-subtle">No referral data</div>
              )}
            </div>
          </div>

          {/* Ad Type table */}
          {adTypeData.length > 0 && (
            <div className="bg-surface rounded-xl border border-border shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-border">
                <h3 className="text-sm font-semibold text-content">Ad Types</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-surface-muted/50">
                    <tr>
                      {['Ad Type', 'Impressions', 'Clicks', 'CTR', 'Share'].map((h, i) => (
                        <th key={h} className={`px-6 py-3 text-xs font-semibold text-content-subtle uppercase tracking-wider ${i === 0 ? 'text-left' : 'text-right'}`}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {adTypeData.map((at) => {
                      const total = adTypeData.reduce((s, a) => s + a.impressions, 0);
                      const share = total > 0 ? ((at.impressions / total) * 100).toFixed(1) : '0.0';
                      const labels: Record<string, string> = { interstitial: 'Interstitial', rewarded: 'Rewarded', banner: 'Banner', native: 'Custom Banner' };
                      const colors: Record<string, string> = { interstitial: 'bg-primary/10', rewarded: 'bg-warning-soft', banner: 'bg-info-soft', native: 'bg-success-soft' };
                      const barColors: Record<string, string> = { interstitial: 'bg-purple-500', rewarded: 'bg-amber-500', banner: 'bg-blue-500', native: 'bg-success' };
                      return (
                        <tr key={at.ad_type} className="hover:bg-surface-muted/50 transition-colors">
                          <td className="px-6 py-3.5">
                            <div className="flex items-center gap-2.5">
                              <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${colors[at.ad_type] || 'bg-surface-muted'}`}>
                                <span className="text-xs font-bold text-content-muted">{(labels[at.ad_type] || at.ad_type).charAt(0)}</span>
                              </div>
                              <span className="text-sm font-medium text-content">{labels[at.ad_type] || at.ad_type}</span>
                            </div>
                          </td>
                          <td className="px-6 py-3.5 text-right text-sm text-content-muted font-mono">{formatNumber(at.impressions)}</td>
                          <td className="px-6 py-3.5 text-right text-sm text-content-muted font-mono">{formatNumber(at.clicks)}</td>
                          <td className="px-6 py-3.5 text-right text-sm text-content-muted font-mono">{at.ctr.toFixed(2)}%</td>
                          <td className="px-6 py-3.5 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-14 bg-surface-muted rounded-full h-1.5">
                                <div className={`h-1.5 rounded-full ${barColors[at.ad_type] || 'bg-content-subtle'}`} style={{ width: `${share}%` }} />
                              </div>
                              <span className="text-xs text-content-subtle font-mono w-10">{share}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Flow Comparison: Force fullscreen webview vs ad-click via stub */}
      <div className="bg-surface rounded-xl border border-border shadow-sm p-5 mt-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-1">
          <h2 className="text-lg font-semibold text-content">
            Flow Comparison: Force vs Ad-click
          </h2>
          <span className="text-xs text-content-subtle">Last {days}d</span>
        </div>
        <p className="text-xs text-content-subtle mb-4">
          <span className="font-medium">Force</span> = fullscreen offer webview on app open ·{' '}
          <span className="font-medium">Ad-click</span> = user played the stub, tapped an ad, then hit the offer
        </p>
        {flowCompare && flowCompare.rows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium text-content-subtle uppercase tracking-wider border-b border-border">
                  <th rowSpan={2} className="py-2 pr-4 align-bottom">App</th>
                  <th colSpan={5} className="pb-1 text-center border-l border-border">Force fullscreen</th>
                  <th colSpan={5} className="pb-1 text-center border-l border-border">Ad-click (stub)</th>
                </tr>
                <tr className="text-left text-xs font-medium text-content-subtle uppercase tracking-wider border-b border-border">
                  <th className="pb-2 pl-2 text-right border-l border-border">Clicks</th>
                  <th className="pb-2 text-right">Reg</th>
                  <th className="pb-2 text-right">Dep</th>
                  <th className="pb-2 text-right">CR<sub>dep</sub></th>
                  <th className="pb-2 text-right pr-2">EPC</th>
                  <th className="pb-2 pl-2 text-right border-l border-border">Clicks</th>
                  <th className="pb-2 text-right">Reg</th>
                  <th className="pb-2 text-right">Dep</th>
                  <th className="pb-2 text-right">CR<sub>dep</sub></th>
                  <th className="pb-2 text-right pr-2">EPC</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {flowCompare.rows.map((r) => {
                  const winner =
                    r.force.cr_dep > r.ad_click.cr_dep
                      ? 'force'
                      : r.ad_click.cr_dep > r.force.cr_dep
                      ? 'ad_click'
                      : null;
                  return (
                    <tr key={r.app_id}>
                      <td className="py-2 pr-4 font-medium text-content truncate max-w-[16rem]">
                        {r.app_name}
                      </td>
                      <td className="py-2 pl-2 text-right text-content-muted font-mono border-l border-border">
                        {formatNumber(r.force.clicks)}
                      </td>
                      <td className="py-2 text-right text-content-muted font-mono">{formatNumber(r.force.regs)}</td>
                      <td className="py-2 text-right text-content-muted font-mono">{formatNumber(r.force.deps)}</td>
                      <td className={`py-2 text-right font-mono ${winner === 'force' ? 'text-success font-semibold' : 'text-content-muted'}`}>
                        {r.force.cr_dep.toFixed(2)}%
                      </td>
                      <td className="py-2 text-right text-content-muted font-mono pr-2">${r.force.epc.toFixed(2)}</td>
                      <td className="py-2 pl-2 text-right text-content-muted font-mono border-l border-border">
                        {formatNumber(r.ad_click.clicks)}
                      </td>
                      <td className="py-2 text-right text-content-muted font-mono">{formatNumber(r.ad_click.regs)}</td>
                      <td className="py-2 text-right text-content-muted font-mono">{formatNumber(r.ad_click.deps)}</td>
                      <td className={`py-2 text-right font-mono ${winner === 'ad_click' ? 'text-success font-semibold' : 'text-content-muted'}`}>
                        {r.ad_click.cr_dep.toFixed(2)}%
                      </td>
                      <td className="py-2 text-right text-content-muted font-mono pr-2">${r.ad_click.epc.toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border text-sm font-semibold text-content bg-surface-muted">
                  <td className="py-2 pr-4">Total</td>
                  <td className="py-2 pl-2 text-right font-mono border-l border-border">
                    {formatNumber(flowCompare.totals.force.clicks)}
                  </td>
                  <td className="py-2 text-right font-mono">{formatNumber(flowCompare.totals.force.regs)}</td>
                  <td className="py-2 text-right font-mono">{formatNumber(flowCompare.totals.force.deps)}</td>
                  <td className="py-2 text-right font-mono">{flowCompare.totals.force.cr_dep.toFixed(2)}%</td>
                  <td className="py-2 text-right font-mono pr-2">${flowCompare.totals.force.epc.toFixed(2)}</td>
                  <td className="py-2 pl-2 text-right font-mono border-l border-border">
                    {formatNumber(flowCompare.totals.ad_click.clicks)}
                  </td>
                  <td className="py-2 text-right font-mono">{formatNumber(flowCompare.totals.ad_click.regs)}</td>
                  <td className="py-2 text-right font-mono">{formatNumber(flowCompare.totals.ad_click.deps)}</td>
                  <td className="py-2 text-right font-mono">{flowCompare.totals.ad_click.cr_dep.toFixed(2)}%</td>
                  <td className="py-2 text-right font-mono pr-2">${flowCompare.totals.ad_click.epc.toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-content-subtle">
            No flow data for this period yet
          </div>
        )}
      </div>
    </div>
  );
}

function getFlagEmoji(countryCode: string): string {
  if (countryCode === 'XX' || countryCode === 'Unknown') return '\u{1F30D}';
  const codePoints = countryCode.toUpperCase().split('').map((c) => 127397 + c.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}
